<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import { useDealStagesStore } from 'dashboard/stores/dealStages';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['success']);

const { t } = useI18n();
const dealsStore = useDealsStore();
const dealStagesStore = useDealStagesStore();

const CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'BRL' },
  { value: 'USD', label: 'USD' },
];

const dialogRef = ref(null);
const isSaving = ref(false);
const showErrors = ref(false);
const dealId = ref(null);

const form = reactive({
  title: '',
  dealStageId: '',
  value: '',
  currency: 'BRL',
  notes: '',
  nextFollowUpOn: '',
});

const stageOptions = computed(() =>
  dealStagesStore.orderedStages.map(stage => ({
    value: stage.id,
    label: stage.name,
  }))
);

const isTitleInvalid = computed(() => showErrors.value && !form.title.trim());

const resetForm = () => {
  dealId.value = null;
  form.title = '';
  form.dealStageId = '';
  form.value = '';
  form.currency = 'BRL';
  form.notes = '';
  form.nextFollowUpOn = '';
  showErrors.value = false;
};

const open = deal => {
  resetForm();
  dealId.value = deal.id;
  form.title = deal.title || '';
  form.dealStageId = deal.dealStageId || '';
  form.value =
    deal.valueCents || deal.valueCents === 0
      ? String(deal.valueCents / 100)
      : '';
  form.currency = deal.currency || 'BRL';
  form.notes = deal.notes || '';
  form.nextFollowUpOn = deal.nextFollowUpOn || '';
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  showErrors.value = true;
  if (!form.title.trim()) return;

  isSaving.value = true;
  try {
    await dealsStore.updateDeal(dealId.value, {
      title: form.title.trim(),
      dealStageId: form.dealStageId || undefined,
      valueCents:
        form.value !== '' ? Math.round(Number(form.value) * 100) : null,
      currency: form.currency || undefined,
      notes: form.notes.trim() || null,
      nextFollowUpOn: form.nextFollowUpOn || null,
    });
    useAlert(t('CRM_FUNIL.EDIT_DEAL.SUCCESS'));
    emit('success');
    closeDialog();
  } catch {
    useAlert(t('CRM_FUNIL.EDIT_DEAL.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open, form, handleConfirm });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CRM_FUNIL.EDIT_DEAL.TITLE') }}
      </span>

      <Input
        v-model="form.title"
        :label="t('CRM_FUNIL.NEW_DEAL.FIELDS.DEAL_TITLE')"
        :message="isTitleInvalid ? t('CRM_FUNIL.NEW_DEAL.TITLE_REQUIRED') : ''"
        :message-type="isTitleInvalid ? 'error' : 'info'"
        :disabled="isSaving"
      />

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('CRM_FUNIL.NEW_DEAL.FIELDS.STAGE') }}
        </label>
        <Select v-model="form.dealStageId" :options="stageOptions" />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <Input
          v-model="form.value"
          type="number"
          min="0"
          :label="t('CRM_FUNIL.NEW_DEAL.FIELDS.VALUE')"
          :disabled="isSaving"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.NEW_DEAL.FIELDS.CURRENCY') }}
          </label>
          <Select v-model="form.currency" :options="CURRENCY_OPTIONS" />
        </div>
      </div>

      <Input
        v-model="form.nextFollowUpOn"
        type="date"
        :label="t('CRM_FUNIL.EDIT_DEAL.FIELDS.FOLLOW_UP')"
        :disabled="isSaving"
      />

      <TextArea
        v-model="form.notes"
        :label="t('CRM_FUNIL.EDIT_DEAL.FIELDS.NOTES')"
        :placeholder="t('CRM_FUNIL.EDIT_DEAL.NOTES_PLACEHOLDER')"
        :disabled="isSaving"
      />
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('CRM_FUNIL.EDIT_DEAL.CANCEL')"
          variant="faded"
          color="slate"
          type="reset"
          class="w-full"
          @click="closeDialog"
        />
        <Button
          :label="t('CRM_FUNIL.EDIT_DEAL.SUBMIT')"
          color="blue"
          type="submit"
          class="w-full"
          :is-loading="isSaving"
          :disabled="isSaving"
        />
      </div>
    </template>
  </Dialog>
</template>
